import { utils, writeFile } from 'xlsx';

interface StatementData {
  opening?: Record<string, string>;
  closing?: Record<string, string>;
  pending?: Record<string, string>;
  available?: Record<string, string>;
  accruedHoldbackVolume?: Record<string, string>;
  netHoldbackVolume?: Record<string, string>;
  fundsReserved?: Record<string, string>;
  rowsOpeningClosing?: Array<
    Array<{
      id: string;
      columns: Record<string, string>;
      isNotZero: boolean;
    }>
  >;
  rowsReversedHoldback?: Array<
    Array<{
      id: string;
      columns: Record<string, string>;
      isNotZero: boolean;
    }>
  >;
}

interface FiltersData {
  reportDate?: string;
  merchant?: string[];
  project?: string[];
  balance?: string[];
  contract?: string[];
  contractWith?: string[];
  reportPeriod?: string[];
}

type ExcelRow = Array<string | number>;

// Маппинг ID полей к названиям строк в Excel
const ROW_LABELS = {
  openingBalance: 'Opening balance',
  payInVolume: 'Pay-in volume',
  payInFee: 'Pay-in fee',
  payOutVolume: 'Pay-out volume',
  payOutFee: 'Pay-out fee',
  refundVolume: 'Refund volume',
  refundFee: 'Refund fee',
  chargebackVolume: 'Chargeback volume',
  chargebackFee: 'Chargeback fee',
  chargebackAdditionalFee: 'Chargeback additional fee',
  otherServiceFees: 'Other service fees',
  fundsTransfer: 'Funds transfer from/to balance',
  transferFee: 'Transfer fee',
  currencyExchange: 'Currency exchange',
  settlement: 'Settlement',
  settlementFee: 'Settlement fee',
  balanceCorrection: 'Balance correction',
  closingBalance: 'Closing Balance',
  holdbackVolume: 'Holdback volume',
  unblockedHoldbackVolume: 'Unblocked holdback volume',
  accruedHoldbackVolume: 'Accrued holdback volume',
  netHoldbackVolume: 'Net holdback volume',
  availableBalance: 'Available balance',
  fundsReserved: 'Funds Reserved',
};

// Порядок строк в Excel
const ROW_ORDER = [
  'openingBalance',
  'payInVolume',
  'payInFee',
  'payOutVolume',
  'payOutFee',
  'refundVolume',
  'refundFee',
  'chargebackVolume',
  'chargebackFee',
  'chargebackAdditionalFee',
  'otherServiceFees',
  'fundsTransfer',
  'transferFee',
  'currencyExchange',
  'settlement',
  'settlementFee',
  'balanceCorrection',
  'closingBalance',
  'holdbackVolume',
  'unblockedHoldbackVolume',
  'accruedHoldbackVolume',
  'netHoldbackVolume',
  'availableBalance',
  'fundsReserved',
];

function generateStatementExcel(
  data: StatementData,
  filtersData?: FiltersData
) {
  const {
    opening,
    closing,
    pending,
    available,
    accruedHoldbackVolume,
    netHoldbackVolume,
    fundsReserved,
    rowsOpeningClosing,
    rowsReversedHoldback,
  } = data;

  const currenciesSet = new Set<string>();

  if (opening) Object.keys(opening).forEach((curr) => currenciesSet.add(curr));
  if (closing) Object.keys(closing).forEach((curr) => currenciesSet.add(curr));

  [...(rowsOpeningClosing || []), ...(rowsReversedHoldback || [])].forEach(
    (section) => {
      section.forEach((item) => {
        if (item.columns) {
          Object.keys(item.columns).forEach((currency) => {
            currenciesSet.add(currency);
          });
        }
      });
    }
  );

  const currencies = Array.from(currenciesSet);

  const excelData: ExcelRow[] = [];

  const headerRow: ExcelRow = ['', ...currencies];
  excelData.push(headerRow);

  const dataMap = new Map<string, Record<string, string>>();
  [...(rowsOpeningClosing || []), ...(rowsReversedHoldback || [])].forEach(
    (section) => {
      section.forEach((item) => {
        dataMap.set(item.id, item.columns || {});
      });
    }
  );

  ROW_ORDER.forEach((rowId) => {
    const rowLabel = ROW_LABELS[rowId];
    const rowData: ExcelRow = [rowLabel];

    currencies.forEach((currency) => {
      let value = '0';

      if (rowId === 'openingBalance' && opening && opening[currency]) {
        value = opening[currency];
      } else if (rowId === 'closingBalance' && closing && closing[currency]) {
        value = closing[currency];
      } else if (
        rowId === 'availableBalance' &&
        available &&
        available[currency]
      ) {
        value = available[currency];
      } else if (
        rowId === 'accruedHoldbackVolume' &&
        accruedHoldbackVolume &&
        accruedHoldbackVolume[currency]
      ) {
        value = accruedHoldbackVolume[currency];
      } else if (
        rowId === 'netHoldbackVolume' &&
        netHoldbackVolume &&
        netHoldbackVolume[currency]
      ) {
        value = netHoldbackVolume[currency];
      } else if (
        rowId === 'fundsReserved' &&
        fundsReserved &&
        fundsReserved[currency]
      ) {
        value = fundsReserved[currency];
      } else {
        const itemData = dataMap.get(rowId);
        if (itemData && itemData[currency]) {
          value = itemData[currency];
        }
      }

      rowData.push(value);
    });

    excelData.push(rowData);
  });

  excelData.push([]);
  excelData.push([]);

  const labelsRow: ExcelRow = ['', 'Merchants', 'Projects', 'Balances'];
  excelData.push(labelsRow);

  let merchantsList = '';
  let projectsList = '';
  let balancesList = '';

  if (filtersData) {
    if (filtersData.merchant && Array.isArray(filtersData.merchant)) {
      merchantsList = filtersData.merchant.join(', ');
    }
    if (filtersData.project && Array.isArray(filtersData.project)) {
      projectsList = filtersData.project.join(', ');
    }
    if (filtersData.balance && Array.isArray(filtersData.balance)) {
      balancesList = filtersData.balance.join(', ');
    }
  }

  const valuesRow: ExcelRow = ['', merchantsList, projectsList, balancesList];
  for (let i = 4; i < currencies.length + 1; i++) {
    valuesRow.push('');
  }
  excelData.push(valuesRow);

  const wb = utils.book_new();
  const ws = utils.aoa_to_sheet(excelData);
  const colWidths = [{ wch: 30 }];
  currencies.forEach(() => {
    colWidths.push({ wch: 15 });
  });
  ws['!cols'] = colWidths;
  utils.book_append_sheet(wb, ws, 'Statement');

  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = String(currentDate.getMonth() + 1).padStart(2, '0');
  const day = String(currentDate.getDate()).padStart(2, '0');
  const hours = String(currentDate.getHours()).padStart(2, '0');
  const minutes = String(currentDate.getMinutes()).padStart(2, '0');
  const seconds = String(currentDate.getSeconds()).padStart(2, '0');
  const filename = `statement_${year}-${month}-${day}_${hours}-${minutes}-${seconds}.xlsx`;

  writeFile(wb, filename);
}

export { generateStatementExcel };
